/**
 * Rax API Client
 *
 * Client-side service for interacting with Rax backend APIs.
 * Handles auth tokens, error handling, and type safety.
 */

// ============================================================================
// Types
// ============================================================================

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface UserInfo {
  id: string;
  email?: string;
  name?: string;
  tier: "free" | "pro" | "band" | "team" | "enterprise";
  budgetLimitCents: number;
  currentSpendCents: number;
  agentCount: number;
  createdAt?: string;
}

export interface OnboardingStatus {
  userId: string;
  status: "not_started" | "in_progress" | "completed";
  currentStep: "welcome" | "mode" | "api_key" | "agent" | "done";
  mode?: "managed" | "byok";
  hasApiKey: boolean;
  firstAgentId?: string;
}

export interface ProviderConfig {
  mode: "managed" | "byok";
  provider?: "anthropic" | "openai" | "openrouter";
  model?: string;
  hasApiKey: boolean;
  availableProviders?: string[];
  selectionRequired?: boolean;
}

export interface CostPreferences {
  userId: string;
  preferredModel: string | null;
  costSensitivity: "low" | "balanced" | "high";
  autoDowngrade: boolean;
  idleDowngradeModel: string;
  maxCostPerTurnCents: number | null;
  updatedAt: string;
}

export type AutonomyLevel = "copilot" | "balanced" | "self_supply";
export type AutonomyPolicyMode = "ask_first" | "on_request" | "always_check" | "proactive_notify";

export interface AutonomyPreferences {
  level: AutonomyLevel;
  actionDefaults: {
    check: AutonomyPolicyMode;
    do: AutonomyPolicyMode;
  };
  taskPolicies: Record<string, AutonomyPolicyMode>;
  updatedAt: string;
}

export interface AutonomyPreferencesPatch {
  level?: AutonomyLevel;
  actionDefaults?: Partial<AutonomyPreferences["actionDefaults"]>;
  taskPolicies?: Record<string, AutonomyPolicyMode>;
}

export type AINewsCategory =
  | "new_model"
  | "new_tool"
  | "research_paper"
  | "product_launch"
  | "api_update"
  | "open_source"
  | "funding"
  | "other";

export interface ProactivePreferences {
  enabled: boolean;
  excitementThreshold: number;
  categories: AINewsCategory[];
  frequencyHours: number;
  preferredChannel: string | null;
  respectQuietHours: boolean;
}

export interface ResearchCycleResult {
  itemsFound: number;
  itemsNew: number;
  itemsExciting: number;
  notificationsSent: number;
  errors: string[];
}

export interface SpendPreferences {
  monthlyHardCapCents: number;
  alertThreshold: number;
  explicitOptUp: boolean;
  pauseOnCap: boolean;
  autoPausedAgentIds: string[];
  autoPausedAt: string | null;
  updatedAt: string;
  defaultLowCapCents: number;
  effectiveLimitCents: number;
  tierLimitCents: number;
  policyLimitCents: number;
  usedCents: number;
  remainingCents: number;
  // Daily spend alerts
  dailyAlertEnabled: boolean;
  dailyAlertLimitCents: number;
  dailyAlertEmail: boolean;
  lastDailyAlertSentAt: string | null;
  dailySpendCents?: number;
}

export interface ApiKeyInfo {
  id: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  createdAt: string;
  lastUsedAt: string | null;
  useCount: number;
  expiresAt: string | null;
  revokedAt: string | null;
}

export interface SecretInfo {
  id: string;
  type: string;
  name: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  expiresAt?: string;
  lastAccessedAt?: string;
  accessCount: number;
}

export type GoogleSkillIntegration = "gmail" | "calendar";

export interface GoogleSkillOAuthStartResult {
  provider: "google";
  integration: GoogleSkillIntegration;
  authorizationUrl: string;
}

export interface RuntimeSecretHealth {
  enabled: boolean;
  ok: boolean;
  message: string;
}

export type SecretActivationStatus =
  | "vault_only"
  | "runtime_broker"
  | "synced_deferred"
  | "synced_immediate";

export interface StoreSecretResult {
  id: string;
  syncedEnvVars?: string[];
  activationStatus?: SecretActivationStatus;
  deploymentTriggered?: boolean;
  brokerSynced?: boolean;
  warning?: string | null;
}

export interface LedgerEntry {
  id: string;
  type:
    | "personal_info"
    | "preference"
    | "vocabulary"
    | "workflow"
    | "correction"
    | "relationship"
    | "schedule"
    | "tool_preference"
    | "custom";
  key: string;
  value: string;
  description?: string;
  confidence: number;
  learnedFrom: "user_stated" | "user_corrected" | "inferred" | "imported" | "admin_set";
  createdAt: string;
  updatedAt: string;
  tags?: string[];
  archived: boolean;
}

export interface LedgerStats {
  total: number;
  byType: Record<string, number>;
  bySource: Record<string, number>;
  archived: number;
}

export interface SessionCost {
  sessionKey: string;
  totalCostUsd: number;
  totalCostDisplay: string;
  llmCalls: number;
  toolCalls: number;
}

export type AutomationTriggerType = "schedule" | "event";
export type AutomationRuleStatus = "active" | "paused" | "disabled";

export interface AutomationRuleDraft {
  name: string;
  triggerType: AutomationTriggerType;
  schedule: string | null;
  eventCondition: string | null;
  action: string;
  sourceMessage: string;
  confidence: number;
}

export interface AutomationRule {
  id: string;
  userId: string;
  agentId: string | null;
  name: string;
  triggerType: AutomationTriggerType;
  triggerConfig: Record<string, unknown>;
  action: string;
  status: AutomationRuleStatus;
  sourceMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export type ApprovalStatus = "pending" | "approved" | "denied" | "expired";
export type ApprovalActionClass = "check" | "do";

export interface ApprovalQueueEntry {
  id: string;
  userId: string;
  agentId: string | null;
  sessionId: string | null;
  taskKey: string;
  actionClass: ApprovalActionClass;
  toolName: string | null;
  reason: string;
  details: Record<string, unknown>;
  status: ApprovalStatus;
  createdAt: string;
  expiresAt: string;
  resolvedAt: string | null;
  resolvedBy: string | null;
}

export interface ApprovalQueueCreateParams {
  taskKey: string;
  actionClass: ApprovalActionClass;
  toolName?: string;
  reason: string;
  agentId?: string;
  sessionId?: string;
  details?: Record<string, unknown>;
  expiresInMs?: number;
}

export interface Agent {
  id: string;
  name: string;
  description?: string;
  icon: string;
  type: "work" | "personal" | "family" | "project" | "custom";
  status: "active" | "paused" | "archived";
  enabledSkills: string[];
  costThisMonth: number;
  lastActive?: string;
  unreadCount?: number;
}

// Persona types (Multi-Agent Teams)
export type PersonaRole =
  | "coordinator"
  | "engineer"
  | "pm"
  | "devops"
  | "analyst"
  | "writer"
  | "support"
  | "custom";
export type PersonaStatus = "active" | "resting" | "archived";

export interface PersonaAppearance {
  hairColor: string;
  hairStyle: "short" | "long" | "bald" | "ponytail";
  outfit: "suit" | "hoodie" | "casual" | "uniform";
  skinTone: number;
}

export interface Persona {
  id: string;
  name: string;
  role: PersonaRole;
  appearance: PersonaAppearance;
  personality: {
    style: string;
    voice: string;
  };
  skills: {
    enabled: string[];
    disabled: string[];
  };
  preferences: {
    model?: string;
    autonomy: "low" | "medium" | "high";
  };
  systemPromptAdditions?: string;
  status: PersonaStatus;
  created: string;
  lastActive: string;
  // Marketplace fields
  author?: string;
  version?: string;
  description?: string;
  downloads?: number;
  rating?: number;
}

export interface PersonaCreateParams {
  name: string;
  role: PersonaRole;
  appearance?: Partial<PersonaAppearance>;
  personality?: {
    style?: string;
    voice?: string;
  };
  skills?: {
    enabled?: string[];
    disabled?: string[];
  };
  preferences?: {
    model?: string;
    autonomy?: "low" | "medium" | "high";
  };
  systemPromptAdditions?: string;
}

export interface PersonaExport {
  name: string;
  role: PersonaRole;
  appearance: PersonaAppearance;
  personality: {
    style: string;
    voice: string;
  };
  skills: {
    enabled: string[];
    disabled: string[];
  };
  preferences: {
    model?: string;
    autonomy: "low" | "medium" | "high";
  };
  systemPromptAdditions?: string;
  author?: string;
  version?: string;
  description?: string;
}

export interface PersonaListing {
  id: string;
  personaId: string;
  name: string;
  role: PersonaRole;
  description: string;
  author: string;
  downloads: number;
  rating: number;
  ratingCount: number;
  appearance: PersonaAppearance;
  createdAt: string;
}

export type AgentShareRole =
  | "viewer"
  | "member"
  | "collaborator"
  | "owner"
  | "admin"
  | "user"
  | "editor";

export interface AgentSharePermission {
  read: boolean;
  write: boolean;
}

export interface AgentShare {
  id?: string;
  agentId: string;
  userId: string;
  role: AgentShareRole;
  skillPermissions?: Record<string, AgentSharePermission | string[]>;
  grantedBy: string;
  grantedAt: string;
  expiresAt?: string;
  email?: string | null;
  name?: string | null;
}

export interface RailwayInstance {
  id: string;
  userId: string;
  provider: "railway" | "flyio" | "kubernetes";
  providerId?: string | null;
  url?: string | null;
  internalUrl?: string | null;
  status: "provisioning" | "running" | "sleeping" | "stopped" | "error";
  region?: string | null;
  createdAt: string;
  lastActiveAt: string;
  config: {
    errorMessage?: string;
    errorTimestamp?: string;
    errorCode?: string;
    railwayServiceId?: string;
    railwayEnvironmentId?: string;
    railwayDeploymentId?: string;
    railwayVolumeId?: string;
    railwayVolumeMountPath?: string;
  };
  storage?: {
    mode: string;
    durable: boolean;
    volumeId?: string | null;
    mountPath?: string | null;
    warning?: string | null;
  };
}

// ============================================================================
// API Client Class
// ============================================================================

export class RaxApi {
  private baseUrl: string;
  private token: string | null = null;

  constructor(baseUrl: string = "") {
    this.baseUrl = baseUrl;
  }

  /**
   * Set the auth token for API requests
   */
  setToken(token: string | null): void {
    this.token = token;
  }

  /**
   * Get the current auth token
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Get a fresh Clerk token (they expire every ~60s)
   */
  private async getFreshToken(): Promise<string | null> {
    try {
      const clerk = (window as any).Clerk || (window as any).clerkClient;
      if (clerk?.session) {
        const token = await clerk.session.getToken();
        if (token) {
          this.token = token;
          return token;
        }
      }
    } catch {
      // Fall back to cached token
    }
    return this.token;
  }

  /**
   * Make an authenticated API request
   */
  async request<T>(method: string, path: string, body?: unknown): Promise<ApiResponse<T>> {
    // Always get a fresh token — Clerk JWTs expire in ~60s
    const token = await this.getFreshToken();

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(`${this.baseUrl}${path}`, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `HTTP ${response.status}`,
          ...(data.code && { code: data.code }),
        } as any;
      }

      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Network error",
      };
    }
  }

  // ==========================================================================
  // User / Auth
  // ==========================================================================

  async getCurrentUser(): Promise<ApiResponse<UserInfo>> {
    return this.request<UserInfo>("GET", "/api/rax/me");
  }

  async getUserCosts(): Promise<
    ApiResponse<{
      userId: string;
      totalCostUsd: number;
      totalCostDisplay: string;
      sessions: SessionCost[];
    }>
  > {
    return this.request("GET", "/api/rax/me/costs");
  }

  // ==========================================================================
  // Onboarding
  // ==========================================================================

  async getOnboardingStatus(): Promise<ApiResponse<OnboardingStatus>> {
    return this.request<OnboardingStatus>("GET", "/api/rax/onboarding/status");
  }

  async startOnboarding(): Promise<ApiResponse<OnboardingStatus>> {
    return this.request<OnboardingStatus>("POST", "/api/rax/onboarding/start");
  }

  async setOnboardingMode(mode: "managed" | "byok"): Promise<ApiResponse<OnboardingStatus>> {
    return this.request<OnboardingStatus>("POST", "/api/rax/onboarding/mode", { mode });
  }

  async submitApiKey(provider: string, apiKey: string): Promise<ApiResponse<OnboardingStatus>> {
    return this.request<OnboardingStatus>("POST", "/api/rax/onboarding/api-key", {
      provider,
      apiKey,
    });
  }

  async createFirstAgent(
    agentName: string,
    agentType?: string,
    persona?: string,
    connectedSkills?: string[],
  ): Promise<
    ApiResponse<{
      agentId: string;
      status: OnboardingStatus;
    }>
  > {
    return this.request("POST", "/api/rax/onboarding/agent", {
      agentName,
      agentType,
      persona,
      connectedSkills,
    });
  }

  async quickStart(): Promise<ApiResponse<OnboardingStatus>> {
    return this.request<OnboardingStatus>("POST", "/api/rax/onboarding/quick-start");
  }

  // ==========================================================================
  // Provider Config
  // ==========================================================================

  async getProviderConfig(): Promise<ApiResponse<ProviderConfig>> {
    return this.request<ProviderConfig>("GET", "/api/rax/provider-config");
  }

  async getCostPreferences(): Promise<ApiResponse<CostPreferences>> {
    return this.request<CostPreferences>("GET", "/api/rax/preferences/cost");
  }

  async getAutonomyPreferences(): Promise<ApiResponse<AutonomyPreferences>> {
    return this.request<AutonomyPreferences>("GET", "/api/rax/preferences/autonomy");
  }

  async updateAutonomyPreferences(
    patch: AutonomyPreferencesPatch,
  ): Promise<ApiResponse<AutonomyPreferences>> {
    return this.request<AutonomyPreferences>("POST", "/api/rax/preferences/autonomy", patch);
  }

  async getSpendPreferences(): Promise<ApiResponse<SpendPreferences>> {
    return this.request<SpendPreferences>("GET", "/api/rax/preferences/spend");
  }

  // ==========================================================================
  // Proactive Research Preferences
  // ==========================================================================

  async getProactivePreferences(): Promise<ApiResponse<ProactivePreferences>> {
    return this.request<ProactivePreferences>("GET", "/api/rax/preferences/proactive");
  }

  async updateProactivePreferences(
    patch: Partial<ProactivePreferences>,
  ): Promise<ApiResponse<ProactivePreferences>> {
    return this.request<ProactivePreferences>("POST", "/api/rax/preferences/proactive", patch);
  }

  async triggerProactiveResearch(): Promise<ApiResponse<ResearchCycleResult>> {
    return this.request<ResearchCycleResult>("POST", "/api/rax/proactive/trigger");
  }

  async listApiKeys(): Promise<ApiResponse<ApiKeyInfo[]>> {
    return this.request<ApiKeyInfo[]>("GET", "/api/rax/api-keys");
  }

  async createApiKey(params: {
    name?: string;
    scopes?: string[];
    expiresAt?: string | null;
  }): Promise<ApiResponse<{ apiKey: ApiKeyInfo; token: string }>> {
    return this.request<{ apiKey: ApiKeyInfo; token: string }>("POST", "/api/rax/api-keys", params);
  }

  async revokeApiKey(id: string): Promise<ApiResponse<{ id: string; revoked: boolean }>> {
    return this.request<{ id: string; revoked: boolean }>(
      "DELETE",
      `/api/rax/api-keys/${encodeURIComponent(id)}`,
    );
  }

  async updateSpendPreferences(patch: {
    monthlyHardCapCents?: number;
    alertThreshold?: number;
    explicitOptUp?: boolean;
    pauseOnCap?: boolean;
    dailyAlertEnabled?: boolean;
    dailyAlertLimitCents?: number;
    dailyAlertEmail?: boolean;
  }): Promise<ApiResponse<SpendPreferences>> {
    return this.request<SpendPreferences>("POST", "/api/rax/preferences/spend", patch);
  }

  async resumeSpendPausedAgents(): Promise<
    ApiResponse<SpendPreferences & { resumedAgentIds: string[] }>
  > {
    return this.request<SpendPreferences & { resumedAgentIds: string[] }>(
      "POST",
      "/api/rax/preferences/spend/resume",
    );
  }

  async setPreferredModel(model: string): Promise<ApiResponse<CostPreferences>> {
    return this.request<CostPreferences>("POST", "/api/rax/preferences/model", { model });
  }

  // ==========================================================================
  // Secrets
  // ==========================================================================

  async listSecrets(): Promise<ApiResponse<SecretInfo[]>> {
    return this.request<SecretInfo[]>("GET", "/api/rax/secrets");
  }

  async getRuntimeSecretHealth(): Promise<ApiResponse<RuntimeSecretHealth>> {
    return this.request<RuntimeSecretHealth>("GET", "/api/rax/secrets/runtime/health");
  }

  async startGoogleSkillOAuth(
    integration: GoogleSkillIntegration,
  ): Promise<ApiResponse<GoogleSkillOAuthStartResult>> {
    return this.request<GoogleSkillOAuthStartResult>("POST", "/api/rax/oauth/google/start", {
      integration,
    });
  }

  async revokeGoogleSkillOAuth(
    integration: GoogleSkillIntegration,
  ): Promise<
    ApiResponse<{
      integration: GoogleSkillIntegration;
      revokedCount: number;
      disconnected: boolean;
    }>
  > {
    return this.request<{
      integration: GoogleSkillIntegration;
      revokedCount: number;
      disconnected: boolean;
    }>("POST", "/api/rax/oauth/google/revoke", { integration });
  }

  async storeSecret(params: {
    type: string;
    name: string;
    value: string;
    metadata?: Record<string, unknown>;
    expiresAt?: string;
  }): Promise<ApiResponse<StoreSecretResult>> {
    return this.request<StoreSecretResult>("POST", "/api/rax/secrets", params);
  }

  async deleteSecret(id: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.request("DELETE", `/api/rax/secrets/${id}`);
  }

  async rotateSecret(id: string, newValue: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.request("POST", `/api/rax/secrets/${id}/rotate`, { newValue });
  }

  async getSecretAccessLogs(limit = 100): Promise<ApiResponse<unknown[]>> {
    return this.request("GET", `/api/rax/secrets/logs?limit=${limit}`);
  }

  // ==========================================================================
  // Learning Ledger
  // ==========================================================================

  async getLedger(options?: {
    includeArchived?: boolean;
    type?: string;
    limit?: number;
  }): Promise<ApiResponse<LedgerEntry[]>> {
    const params = new URLSearchParams();
    if (options?.includeArchived) params.set("includeArchived", "true");
    if (options?.type) params.set("type", options.type);
    if (options?.limit) params.set("limit", String(options.limit));

    const query = params.toString();
    return this.request<LedgerEntry[]>("GET", `/api/rax/ledger${query ? `?${query}` : ""}`);
  }

  async getLedgerStats(): Promise<ApiResponse<LedgerStats>> {
    return this.request<LedgerStats>("GET", "/api/rax/ledger/stats");
  }

  async searchLedger(query: string): Promise<ApiResponse<LedgerEntry[]>> {
    return this.request<LedgerEntry[]>(
      "GET",
      `/api/rax/ledger/search?q=${encodeURIComponent(query)}`,
    );
  }

  async getLedgerEntry(id: string): Promise<ApiResponse<LedgerEntry>> {
    return this.request<LedgerEntry>("GET", `/api/rax/ledger/${id}`);
  }

  async addLedgerEntry(params: {
    type: string;
    key: string;
    value: string;
    description?: string;
    tags?: string[];
  }): Promise<ApiResponse<LedgerEntry>> {
    return this.request<LedgerEntry>("POST", "/api/rax/ledger", params);
  }

  async updateLedgerEntry(
    id: string,
    params: {
      value?: string;
      description?: string;
      confidence?: number;
      tags?: string[];
    },
  ): Promise<ApiResponse<LedgerEntry>> {
    return this.request<LedgerEntry>("PUT", `/api/rax/ledger/${id}`, params);
  }

  async deleteLedgerEntry(id: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.request("DELETE", `/api/rax/ledger/${id}`);
  }

  async archiveLedgerEntry(id: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.request("POST", `/api/rax/ledger/${id}/archive`);
  }

  async exportLedger(): Promise<string> {
    const response = await fetch(`${this.baseUrl}/api/rax/ledger/export`, {
      headers: this.token ? { Authorization: `Bearer ${this.token}` } : {},
    });
    return response.text();
  }

  async importLedger(data: unknown, merge = true): Promise<ApiResponse<{ imported: number }>> {
    return this.request("POST", "/api/rax/ledger/import", { data, merge });
  }

  // ==========================================================================
  // Automation Rules
  // ==========================================================================

  async draftAutomationRule(
    message: string,
    agentId?: string,
  ): Promise<
    ApiResponse<{
      detected: boolean;
      draft?: AutomationRuleDraft;
    }>
  > {
    return this.request("POST", "/api/rax/automations/draft", { message, agentId });
  }

  async listAutomationRules(): Promise<ApiResponse<AutomationRule[]>> {
    return this.request("GET", "/api/rax/automations");
  }

  async createAutomationRule(payload: {
    name: string;
    triggerType: AutomationTriggerType;
    schedule?: string;
    eventCondition?: string;
    action: string;
    sourceMessage?: string;
    agentId?: string;
  }): Promise<ApiResponse<AutomationRule>> {
    return this.request("POST", "/api/rax/automations", payload);
  }

  async updateAutomationRuleStatus(
    id: string,
    status: AutomationRuleStatus,
  ): Promise<ApiResponse<AutomationRule>> {
    return this.request("POST", `/api/rax/automations/${encodeURIComponent(id)}/status`, {
      status,
    });
  }

  async deleteAutomationRule(id: string): Promise<ApiResponse<{ id: string; deleted: boolean }>> {
    return this.request("DELETE", `/api/rax/automations/${encodeURIComponent(id)}`);
  }

  // ==========================================================================
  // Approval Queue
  // ==========================================================================

  async listApprovals(params?: {
    agentId?: string;
    status?: ApprovalStatus;
    limit?: number;
  }): Promise<ApiResponse<ApprovalQueueEntry[]> & { meta?: { pendingCount: number } }> {
    const searchParams = new URLSearchParams();
    if (params?.agentId) searchParams.set("agentId", params.agentId);
    if (params?.status) searchParams.set("status", params.status);
    if (params?.limit) searchParams.set("limit", String(params.limit));
    const queryString = searchParams.toString();
    return this.request("GET", `/api/rax/approvals${queryString ? `?${queryString}` : ""}`);
  }

  async getApprovalCount(): Promise<ApiResponse<{ count: number }>> {
    return this.request("GET", "/api/rax/approvals/count");
  }

  async getApproval(id: string): Promise<ApiResponse<ApprovalQueueEntry>> {
    return this.request("GET", `/api/rax/approvals/${encodeURIComponent(id)}`);
  }

  async resolveApproval(
    id: string,
    decision: "approve" | "deny",
  ): Promise<ApiResponse<ApprovalQueueEntry>> {
    return this.request("POST", `/api/rax/approvals/${encodeURIComponent(id)}/resolve`, {
      decision,
    });
  }

  async createApproval(params: ApprovalQueueCreateParams): Promise<ApiResponse<ApprovalQueueEntry>> {
    return this.request("POST", "/api/rax/approvals", params);
  }

  // ==========================================================================
  // Agents (Band)
  // ==========================================================================

  async listAgents(): Promise<ApiResponse<Agent[]>> {
    return this.request<Agent[]>("GET", "/api/band");
  }

  async getAgent(id: string): Promise<ApiResponse<Agent>> {
    return this.request<Agent>("GET", `/api/band/${id}`);
  }

  async createAgent(params: {
    name: string;
    description?: string;
    icon?: string;
    type?: string;
    skills?: string[];
  }): Promise<ApiResponse<Agent>> {
    return this.request<Agent>("POST", "/api/band", params);
  }

  async updateAgent(
    id: string,
    params: {
      name?: string;
      description?: string;
      icon?: string;
      skills?: string[];
    },
  ): Promise<ApiResponse<Agent>> {
    return this.request<Agent>("PATCH", `/api/band/${id}`, params);
  }

  async pauseAgent(id: string): Promise<ApiResponse<Agent>> {
    return this.request<Agent>("POST", `/api/band/${id}/pause`);
  }

  async resumeAgent(id: string): Promise<ApiResponse<Agent>> {
    return this.request<Agent>("POST", `/api/band/${id}/resume`);
  }

  async archiveAgent(id: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.request("POST", `/api/band/${id}/archive`);
  }

  async enableSkill(
    agentId: string,
    skillId: string,
  ): Promise<ApiResponse<{ enabledSkills: string[] }>> {
    return this.request(
      "POST",
      `/api/band/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}`,
    );
  }

  async disableSkill(
    agentId: string,
    skillId: string,
  ): Promise<ApiResponse<{ enabledSkills: string[] }>> {
    return this.request(
      "DELETE",
      `/api/band/${encodeURIComponent(agentId)}/skills/${encodeURIComponent(skillId)}`,
    );
  }

  async listAgentShares(agentId: string): Promise<ApiResponse<AgentShare[]>> {
    return this.request("GET", `/api/band/${encodeURIComponent(agentId)}/share`);
  }

  async shareAgent(
    agentId: string,
    params: {
      email: string;
      role: AgentShareRole;
      skillPermissions?: Record<string, AgentSharePermission | string[]>;
    },
  ): Promise<ApiResponse<AgentShare>> {
    return this.request("POST", `/api/band/${encodeURIComponent(agentId)}/share`, params);
  }

  async removeAgentShare(
    agentId: string,
    shareUserId: string,
  ): Promise<ApiResponse<{ success: boolean }>> {
    return this.request(
      "DELETE",
      `/api/band/${encodeURIComponent(agentId)}/share/${encodeURIComponent(shareUserId)}`,
    );
  }

  // ==========================================================================
  // Session Costs
  // ==========================================================================

  async getSessionCost(sessionKey: string): Promise<ApiResponse<SessionCost>> {
    return this.request<SessionCost>("GET", `/api/rax/session/${sessionKey}/cost`);
  }

  // ==========================================================================
  // Railway Instances
  // ==========================================================================

  async getInstance(): Promise<ApiResponse<RailwayInstance | null>> {
    return this.request<RailwayInstance | null>("GET", "/api/rax/instance");
  }

  async createInstance(): Promise<ApiResponse<RailwayInstance>> {
    return this.request<RailwayInstance>("POST", "/api/rax/instance");
  }

  async deleteInstance(): Promise<ApiResponse<{ success: boolean }>> {
    return this.request("DELETE", "/api/rax/instance");
  }

  async wakeInstance(): Promise<ApiResponse<{ success: boolean }>> {
    return this.request("POST", "/api/rax/instance/wake");
  }

  // ==========================================================================
  // Workspace Files (Agent Config Editor)
  // ==========================================================================

  async listWorkspaceFiles(
    autoBootstrap = true,
  ): Promise<
    ApiResponse<
      Array<{ name: string; exists: boolean; size?: number; source?: string; error?: string }>
    >
  > {
    const query = autoBootstrap ? "?autoBootstrap=true" : "";
    return this.request("GET", `/api/rax/workspace/files${query}`);
  }

  async bootstrapWorkspaceFiles(): Promise<
    ApiResponse<{ bootstrapped: string[]; skipped: string[]; message: string }>
  > {
    return this.request("POST", "/api/rax/workspace/bootstrap");
  }

  async importWorkspaceFilesFromInstance(): Promise<
    ApiResponse<{ imported: string[]; skipped: string[]; failed: string[]; message: string }>
  > {
    return this.request("POST", "/api/rax/workspace/import");
  }

  async readWorkspaceFile(filename: string): Promise<
    ApiResponse<{
      name: string;
      content: string;
      warning?: string;
      source?: string;
    }>
  > {
    return this.request("GET", `/api/rax/workspace/files/${encodeURIComponent(filename)}`);
  }

  async writeWorkspaceFile(
    filename: string,
    content: string,
    previousContent?: string,
  ): Promise<ApiResponse<{ name: string; saved: boolean }>> {
    return this.request("PUT", `/api/rax/workspace/files/${encodeURIComponent(filename)}`, {
      content,
      previousContent,
    });
  }

  async getFileVersions(
    filename: string,
    limit = 50,
  ): Promise<
    ApiResponse<
      Array<{
        id: string;
        filename: string;
        version_number: number;
        is_known_good: boolean;
        created_at: string;
        created_by: string;
        content_length: number;
      }>
    >
  > {
    return this.request(
      "GET",
      `/api/rax/workspace/files/${encodeURIComponent(filename)}/versions?limit=${limit}`,
    );
  }

  async getFileVersion(
    filename: string,
    versionId: string,
  ): Promise<
    ApiResponse<{
      id: string;
      filename: string;
      content: string;
      previous_content: string | null;
      version_number: number;
      is_known_good: boolean;
      created_at: string;
      created_by: string;
    }>
  > {
    return this.request(
      "GET",
      `/api/rax/workspace/files/${encodeURIComponent(filename)}/versions/${encodeURIComponent(versionId)}`,
    );
  }

  async revertFileVersion(
    filename: string,
    versionId: string,
  ): Promise<ApiResponse<{ name: string; reverted: boolean; content: string }>> {
    return this.request(
      "POST",
      `/api/rax/workspace/files/${encodeURIComponent(filename)}/versions/${encodeURIComponent(versionId)}/revert`,
    );
  }

  async markVersionKnownGood(
    filename: string,
    versionId: string,
  ): Promise<ApiResponse<{ marked: boolean }>> {
    return this.request(
      "POST",
      `/api/rax/workspace/files/${encodeURIComponent(filename)}/versions/${encodeURIComponent(versionId)}/mark-good`,
    );
  }

  // ==========================================================================
  // Health
  // ==========================================================================

  async health(): Promise<ApiResponse<{ status: string; version: string }>> {
    return this.request("GET", "/api/rax/health");
  }

  // ==========================================================================
  // Personas (Multi-Agent Teams)
  // ==========================================================================

  async listPersonas(): Promise<ApiResponse<Persona[]>> {
    return this.request("GET", "/api/rax/personas");
  }

  async getPersona(id: string): Promise<ApiResponse<Persona>> {
    return this.request("GET", `/api/rax/personas/${encodeURIComponent(id)}`);
  }

  async createPersona(params: PersonaCreateParams): Promise<ApiResponse<Persona>> {
    return this.request("POST", "/api/rax/personas", params);
  }

  async updatePersona(
    id: string,
    params: Partial<PersonaCreateParams>,
  ): Promise<ApiResponse<Persona>> {
    return this.request("PATCH", `/api/rax/personas/${encodeURIComponent(id)}`, params);
  }

  async deletePersona(id: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.request("DELETE", `/api/rax/personas/${encodeURIComponent(id)}`);
  }

  async setPersonaStatus(id: string, status: PersonaStatus): Promise<ApiResponse<Persona>> {
    return this.request("POST", `/api/rax/personas/${encodeURIComponent(id)}/status`, { status });
  }

  async wakePersona(id: string): Promise<ApiResponse<Persona>> {
    return this.setPersonaStatus(id, "active");
  }

  async restPersona(id: string): Promise<ApiResponse<Persona>> {
    return this.setPersonaStatus(id, "resting");
  }

  async archivePersona(id: string): Promise<ApiResponse<Persona>> {
    return this.setPersonaStatus(id, "archived");
  }

  async exportPersona(id: string): Promise<ApiResponse<PersonaExport>> {
    return this.request("GET", `/api/rax/personas/${encodeURIComponent(id)}/export`);
  }

  async importPersona(data: PersonaExport): Promise<ApiResponse<Persona>> {
    return this.request("POST", "/api/rax/personas/import", data);
  }

  // Persona marketplace (future)
  async listMarketplacePersonas(params?: {
    category?: string;
    sort?: string;
  }): Promise<ApiResponse<PersonaListing[]>> {
    const query = new URLSearchParams();
    if (params?.category) query.set("category", params.category);
    if (params?.sort) query.set("sort", params.sort);
    const qs = query.toString();
    return this.request("GET", `/api/rax/personas/marketplace${qs ? `?${qs}` : ""}`);
  }

  async downloadMarketplacePersona(listingId: string): Promise<ApiResponse<Persona>> {
    return this.request(
      "POST",
      `/api/rax/personas/marketplace/${encodeURIComponent(listingId)}/download`,
    );
  }

  async sharePersonaToMarketplace(
    id: string,
    params: { description: string },
  ): Promise<ApiResponse<PersonaListing>> {
    return this.request("POST", `/api/rax/personas/${encodeURIComponent(id)}/share`, params);
  }

  // ============================================================================
  // Channel Polling Status
  // ============================================================================

  async getPollingStatus(): Promise<
    ApiResponse<{
      channels: Array<{
        channelId: string;
        pollingFrequency: string | null;
        pollingIntervalMinutes: number | null;
        lastPollAt: string | null;
        nextPollAt: string | null;
        secondsUntilNextPoll: number;
        isDue: boolean;
      }>;
      nextPoll: {
        channelId: string;
        secondsUntilNextPoll: number;
        nextPollAt: string | null;
      } | null;
      hasActivePolling: boolean;
    }>
  > {
    return this.request("GET", "/api/rax/channels/polling-status");
  }

  // ============================================================================
  // Agent Status (for external message processing)
  // ============================================================================

  async getAgentStatus(): Promise<
    ApiResponse<{
      isProcessing: boolean;
      channel: string | null;
      from: string | null;
      startedAt: string | null;
      currentTool: string | null;
    } | null>
  > {
    return this.request("GET", "/api/rax/channels/agent-status");
  }

  // ==========================================================================
  // Data Export
  // ==========================================================================

  /**
   * Export audit logs in the specified format.
   * Downloads as a file attachment.
   */
  async exportAuditLogs(format: "json" | "csv" | "jsonl" = "json", limit = 5000): Promise<Blob> {
    const token = await this.getFreshToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(
      `${this.baseUrl}/api/rax/audit/export?format=${format}&limit=${limit}`,
      { headers },
    );

    if (!response.ok) {
      throw new Error(`Export failed: ${response.status}`);
    }

    return response.blob();
  }

  /**
   * Export learning ledger as JSON.
   * Downloads as a file attachment.
   */
  async exportLedgerData(): Promise<Blob> {
    const token = await this.getFreshToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}/api/rax/ledger/export`, { headers });

    if (!response.ok) {
      throw new Error(`Export failed: ${response.status}`);
    }

    return response.blob();
  }

  /**
   * Export all user data as a ZIP archive.
   * Includes conversations, audit logs, ledger, and preferences.
   */
  async exportAllData(): Promise<Blob> {
    const token = await this.getFreshToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}/api/rax/export/all`, { headers });

    if (!response.ok) {
      throw new Error(`Export failed: ${response.status}`);
    }

    return response.blob();
  }

  // ==========================================================================
  // Account Deletion (GDPR)
  // ==========================================================================

  /**
   * Get a preview of what data will be deleted.
   * Shows record counts for each data type.
   */
  async getDeletionPreview(): Promise<
    ApiResponse<{
      preview: Record<string, number>;
      totalRecords: number;
      warning: string;
    }>
  > {
    return this.request("GET", "/api/rax/account/deletion-preview");
  }

  /**
   * Permanently delete user account and all associated data.
   * Requires confirmation phrase "DELETE MY DATA".
   */
  async deleteAccount(confirmation: string): Promise<
    ApiResponse<{
      deleted: boolean;
      deletedData: Record<string, number>;
      message: string;
    }>
  > {
    return this.request("DELETE", "/api/rax/account", { confirmation });
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

export const raxApi = new RaxApi();

// For use in browser, try to get token from Clerk if available
if (typeof window !== "undefined") {
  // Wait for Clerk to be ready via the promise set in rax.html
  const clerkReady = (window as any).__clerkReady;
  if (clerkReady && typeof clerkReady.then === "function") {
    clerkReady
      .then((clerk: any) => {
        if (clerk?.session) {
          clerk.session.getToken().then((token: string) => {
            if (token) {
              raxApi.setToken(token);
            }
          });
        }
      })
      .catch(() => {
        // Clerk not available, continue without token
      });
  }
}
